/**
 * The host's own API: the organizations on this server and the operators who manage them. Called only
 * from a host operator's own session, outside every organization, so `request` sends the host token.
 */
import type {
  CreateHostUserRequest,
  CreateTenantRequest,
  CurrentTenantDto,
  HostDashboardDto,
  HostEventDto,
  HostUserDto,
  ResetHostPasswordRequest,
  TenantDto,
  UpdateHostUserRequest,
  UpdateTenantRequest
} from '../contracts.gen.js';
import { request } from './http.js';

/** The server and every organization's health, for the host's landing page. */
export function dashboard(signal?: AbortSignal): Promise<HostDashboardDto> {
  return request<HostDashboardDto>('/api/host/dashboard', signal ? { signal } : {});
}

export function tenants(signal?: AbortSignal): Promise<TenantDto[]> {
  return request<TenantDto[]>('/api/host/tenants', signal ? { signal } : {});
}

/** A tenant that was created but did not start comes back with a sentence beside it. */
export async function createTenant(body: CreateTenantRequest): Promise<{ tenant: TenantDto; warning: string | null }> {
  const answer = await request<TenantDto | { tenant: TenantDto; error: string }>('/api/host/tenants', { body });
  return 'tenant' in answer ? { tenant: answer.tenant, warning: answer.error } : { tenant: answer, warning: null };
}

export function updateTenant(id: number, body: UpdateTenantRequest): Promise<TenantDto> {
  return request<TenantDto>(`/api/host/tenants/${id}`, { method: 'PUT', body });
}

export function setSuspended(id: number, suspended: boolean, reason: string | null): Promise<TenantDto> {
  return request<TenantDto>(`/api/host/tenants/${id}/status`, { body: { suspended, reason } });
}

export function restartTenant(id: number): Promise<TenantDto> {
  return request<TenantDto>(`/api/host/tenants/${id}/restart`, { body: {} });
}

export function deleteTenant(id: number, confirmName: string): Promise<void> {
  return request<void>(`/api/host/tenants/${id}/delete`, { body: { confirmName } });
}

export function users(signal?: AbortSignal): Promise<HostUserDto[]> {
  return request<HostUserDto[]>('/api/host/users', signal ? { signal } : {});
}

export function createUser(body: CreateHostUserRequest): Promise<HostUserDto> {
  return request<HostUserDto>('/api/host/users', { body });
}

export function updateUser(id: number, body: UpdateHostUserRequest): Promise<HostUserDto> {
  return request<HostUserDto>(`/api/host/users/${id}`, { method: 'PUT', body });
}

export function resetUserPassword(id: number, body: ResetHostPasswordRequest): Promise<HostUserDto> {
  return request<HostUserDto>(`/api/host/users/${id}/password`, { body });
}

/** The signed-in operator's own permissions and organizations. */
export function myAccess(signal?: AbortSignal): Promise<HostUserDto> {
  return request<HostUserDto>('/api/host/auth/access', signal ? { signal } : {});
}

export function deleteUser(id: number): Promise<void> {
  return request<void>(`/api/host/users/${id}`, { method: 'DELETE' });
}

export function events(limit = 50, signal?: AbortSignal): Promise<HostEventDto[]> {
  return request<HostEventDto[]>(`/api/host/events?limit=${limit}`, signal ? { signal } : {});
}

/** The organization a staff session is working in. */
export function currentTenant(signal?: AbortSignal): Promise<CurrentTenantDto> {
  return request<CurrentTenantDto>('/api/tenant/current', signal ? { signal } : {});
}
